import { useWorkspaceText } from './interface-copy';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { PROJECT_TOOL_CARDS, ProjectToolCard } from './projects/tool-cards';
import { chatProject } from './projects/place';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import {
  useChatCopy,
  useChatInterfaceLocale,
  type ChatCopy,
  type ChatCopyKey,
} from '@/lib/chat-i18n';
import connectionsCopy from '@/i18n/ns/connections';
import { overlaysCopy } from '@/i18n/ns/overlays';
import skillsCopy from '@/i18n/ns/skills';
import { useQuestionCopy } from './question-copy';
import { VoiceControlCard } from '@/voice/voice-control-card';
import type { ReactNode } from 'react';
import type { WithdrawResult } from '@/voice/contracts';
import { useOptionalWorkspace } from './context';
import { ConnectionActionCard } from './connection-action-card';
import { WithdrawCard } from './withdraw-card';
import { WorkspaceIcon } from './icons';
import {
  ChatConnectionSetupCard,
  ChatConnections,
  isChatConnectionOffer,
  isChatConnectionSetup,
} from './chat-connections';
import { AppSecretCard, isAppSecretRequest } from './app-secret-card';
import { ASK_USER_QUESTION, type AskedQuestion } from './question-contract';
import { reopenAsk, useAskState } from './question-dock';
import type { CompanyResult, CompanySkill } from './types';
import { PageProposalChatCard } from './knowledge-pages';
import {
  EXTERNAL_MCP_TOOL,
  ExternalMcpCard,
  SETUP_LINK,
  isExternalMcpPointer,
  openSetupLink,
} from './external-mcp-card';
import { SettingsChangeCard } from './settings-change-card';
import { StorefrontDesignCard } from './storefront/design-card';
import { SETTINGS_TOOL } from '@/lib/account-settings';
import { routeFor } from './navigation';
import type { WorkspaceView } from './view-definitions';

import { describeSchedule } from './agents/schedule';
import type { AgentSchedule, AgentScope } from './agents/api';

// create_agent (collaboration v1, PR 4 §8): the agent it made, switched off.
interface CreatedAgent {
  id: string;
  name: string;
  scope: AgentScope;
  enabled: boolean;
  schedule: AgentSchedule | null;
  skill: { id: string; name: string | null } | null;
}
function CreatedAgentCard({ agent }: { agent: CreatedAgent }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useOptionalWorkspace()!;
  const scheduleCopy = useNamespaceCopy(overlaysCopy);
  const locale = useChatInterfaceLocale();
  return (
    <button
      className="ws-artifact-card"
      onClick={() => ws.openCanvas({ kind: 'agent', id: agent.id })}
    >
      <WorkspaceIcon name="agents" size={22} />
      <span>
        <strong>{agent.name}</strong>
        <small>
          {uiText('Agent ·')}
          {agent.scope === 'company'
            ? uiText('Everyone at {value1}', { value1: ws.companyName })
            : uiText('Only you')}{' '}
          · {describeSchedule(agent.schedule, scheduleCopy, locale)}
          {/* Only a schedule is switched on or off; an on-demand agent runs when asked. */}
          {agent.schedule ? ` · ${agent.enabled ? 'On' : 'Off'}` : ''}
        </small>
      </span>
      <WorkspaceIcon name="arrow" size={15} />
    </button>
  );
}

const names = new Set([
  ASK_USER_QUESTION,
  'create_skill_draft',
  'update_skill_draft',
  'save_work_result',
  'propose_company_knowledge',
  'offer_connections',
  'propose_knowledge_change',
  'suggest_page_edits',
  'create_agent',
  EXTERNAL_MCP_TOOL,
  'voice_control',
  'withdraw_pending_proposal',
  // Offered only while collaboration is on (src/company-workspace/control/).
  SETTINGS_TOOL,
  // A store's design saved like an App: the version it made, opening the storefront canvas.
  'change_storefront_design',
  // Projects v1: a drafted project, a suggested move, and the receipts of a move or an add.
  ...PROJECT_TOOL_CARDS,
  // An App's API key or token, typed into a secure field that never enters the chat.
  'request_app_secret',
]);
export const isWorkspaceToolName = (name: string) => names.has(name);

type ApprovalKind = 'connection_action' | 'custom_mcp_action';
/** The connector calls that wait for a person's approval, and the voice ref kind of each. */
const APPROVAL_KINDS: Readonly<Record<string, ApprovalKind>> = {
  call_connection_tool: 'connection_action',
  call_custom_mcp_tool: 'custom_mcp_action',
};
const approvalKind = (toolName: string, result: unknown): ApprovalKind | null =>
  Object.hasOwn(APPROVAL_KINDS, toolName) &&
  !!result &&
  typeof result === 'object' &&
  (result as { status?: unknown }).status === 'approval_required'
    ? APPROVAL_KINDS[toolName]
    : null;

/**
 * Cards chosen by a tool's output rather than its name (BUILD 2 C2-7): a write
 * staged on a voice turn, whichever write tool it was, and a connector call
 * (Pipedream or custom MCP) awaiting approval. Every other call keeps
 * `fallback`, its default rendering.
 */
export function WorkspaceOutputCard({
  toolName,
  toolCallId,
  messageId,
  result,
  fallback,
}: {
  toolName: string;
  toolCallId?: string;
  messageId?: string;
  result?: unknown;
  fallback: ReactNode;
}) {
  const kind = approvalKind(toolName, result);
  if (kind)
    return (
      <ConnectionActionCard
        kind={kind}
        toolCallId={toolCallId}
        messageId={messageId}
        output={result}
        fallback={fallback}
      />
    );
  return fallback;
}

// The transcript is a record of what was said, so an ask leaves one muted line
// here and nothing else: the panel lives above the composer, and the user's own
// message below IS the answer. The line stays neutral — that a question is no
// longer the pending one says nothing about whether it was answered, and the
// reply itself is never classified to guess. Only a pending ask whose panel was
// put away offers a way back to it.
function QuestionMarker({
  questions,
  language,
  toolCallId,
}: {
  questions: AskedQuestion[];
  language?: string;
  toolCallId?: string;
}) {
  const { reopenable } = useAskState(toolCallId);
  const { locale, copy } = useQuestionCopy(language);
  return (
    <div className="ws-ask-marker" lang={locale}>
      <WorkspaceIcon name="chat" size={13} />
      <span>
        {formatCopy(questions.length === 1 ? copy.askedOne : copy.askedMany, {
          count: questions.length,
        })}
      </span>
      {reopenable && (
        <button type="button" onClick={() => reopenAsk(toolCallId!)}>
          {copy.answer}
        </button>
      )}
    </div>
  );
}

// DES1 HC-08: the tool cards' own lines come from the chat catalog in the reader's language.
function progressCopy(toolName: string, copy: ChatCopy) {
  if (toolName === SETTINGS_TOOL) return copy.toolProgressSettings;
  if (toolName === 'create_agent') return copy.toolProgressAgent;
  if (toolName === 'offer_connections') return copy.toolProgressConnections;
  if (toolName === 'save_work_result') return copy.toolProgressResult;
  if (toolName === 'propose_company_knowledge') return copy.toolProgressClarification;
  if (toolName === 'propose_knowledge_change' || toolName === 'suggest_page_edits')
    return copy.toolProgressKnowledge;
  if (toolName === ASK_USER_QUESTION) return copy.toolProgressQuestion;
  return copy.toolProgressSkill;
}

function errorCopy(toolName: string, copy: ChatCopy) {
  if (toolName === SETTINGS_TOOL) return copy.toolErrorSettings;
  if (toolName === 'create_agent') return copy.toolErrorAgent;
  if (toolName === 'offer_connections') return copy.toolErrorConnections;
  if (toolName === 'save_work_result') return copy.toolErrorResult;
  if (toolName === 'propose_company_knowledge') return copy.toolErrorClarification;
  if (toolName === 'propose_knowledge_change' || toolName === 'suggest_page_edits')
    return copy.toolErrorKnowledge;
  if (toolName === ASK_USER_QUESTION) return copy.toolErrorQuestion;
  return copy.toolErrorSkill;
}

/** The running (or stopped) line of a tool card, in the reader's language. */
function ToolProgressText({ toolName, incomplete }: { toolName: string; incomplete: boolean }) {
  const copy = useChatCopy();
  return <>{incomplete ? copy.toolStepIncomplete : progressCopy(toolName, copy)}</>;
}

export function WorkspaceToolCard({
  toolName,
  toolCallId,
  result,
  status,
}: {
  toolName: string;
  toolCallId?: string;
  result?: unknown;
  status?: { type?: string };
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useOptionalWorkspace();
  const skills = useNamespaceCopy(skillsCopy);
  if (!ws || !isWorkspaceToolName(toolName)) return null;
  if (toolName === 'voice_control')
    return <VoiceControlCard toolCallId={toolCallId} result={result} />;
  if (toolName === 'withdraw_pending_proposal')
    return <WithdrawCard output={result as WithdrawResult | undefined} />;
  if (toolName === 'change_storefront_design')
    return <StorefrontDesignCard result={result} incomplete={status?.type === 'incomplete'} />;
  // A suggestion nobody asked for says nothing when it is refused (already suggested, already placed).
  if (
    toolName === 'suggest_project_move' &&
    result &&
    typeof result === 'object' &&
    (result as { status?: string }).status === 'error'
  )
    return null;
  if (PROJECT_TOOL_CARDS.has(toolName))
    return result &&
      typeof result === 'object' &&
      (result as { status?: string }).status !== 'error' ? (
      <ProjectToolCard toolName={toolName} result={result} />
    ) : result ? (
      <ToolErrorCard toolName={toolName} />
    ) : (
      <div className="ws-tool-progress" role="status">
        <WorkspaceIcon name="clock" size={14} />
        {status?.type === 'incomplete'
          ? uiText('Stopped before it finished.')
          : toolName === 'draft_project'
            ? uiText('Drafting the project…')
            : toolName === 'suggest_project_move'
              ? uiText('Checking where this chat belongs…')
              : toolName === 'move_chat_to_project'
                ? uiText('Moving the chat…')
                : toolName === 'remove_from_project'
                  ? uiText('Taking it out of the project…')
                  : uiText('Adding it to the project…')}
      </div>
    );
  const output =
    result && typeof result === 'object'
      ? (result as {
          status?: string;
          error?: string;
          skill?: CompanySkill;
          result?: CompanyResult;
          proposal?: { id: string };
          questions?: AskedQuestion[];
          language?: string;
          agent?: CreatedAgent;
          appSecretRequest?: unknown;
        })
      : null;
  if (!output)
    return (
      <div className="ws-tool-progress" role="status">
        <WorkspaceIcon name="clock" size={14} />
        {toolName === EXTERNAL_MCP_TOOL && status?.type !== 'incomplete' ? (
          <ConnectedAgentsProgress />
        ) : (
          <ToolProgressText toolName={toolName} incomplete={status?.type === 'incomplete'} />
        )}
      </div>
    );
  // A failed Connected agents read never implies the state loaded and never re-runs the tool:
  // it only points to the Connections setup.
  if (output.status === 'error' && toolName === EXTERNAL_MCP_TOOL)
    return <ConnectedAgentsLoadError />;
  if (output.status === 'error') return <ToolErrorCard toolName={toolName} />;
  // These cards treat the transcript output as a pointer and read live state.
  if (toolName === 'propose_knowledge_change' || toolName === 'suggest_page_edits')
    return <PageProposalChatCard output={output} />;
  if (toolName === 'offer_connections')
    return isChatConnectionOffer(output) && output.items.length ? (
      <ChatConnections offer={output} />
    ) : isChatConnectionSetup(output) ? (
      <ChatConnectionSetupCard offer={output} />
    ) : (
      <div className="ws-error" role="status">
        {uiText('No matching connections are available right now.')}
      </div>
    );
  if (toolName === EXTERNAL_MCP_TOOL)
    return isExternalMcpPointer(output) ? <ExternalMcpCard /> : null;
  if (toolName === SETTINGS_TOOL)
    return <SettingsChangeCard output={output} toolCallId={toolCallId} />;

  if (toolName === 'create_agent')
    return output.agent ? <CreatedAgentCard agent={output.agent} /> : null;
  if (output.appSecretRequest !== undefined)
    return isAppSecretRequest(output.appSecretRequest) ? (
      <AppSecretCard request={output.appSecretRequest} />
    ) : (
      <ToolErrorCard toolName={toolName} />
    );
  if (output.questions)
    return (
      <QuestionMarker
        questions={output.questions}
        language={output.language}
        toolCallId={toolCallId}
      />
    );
  // An earlier chat's suggested company fact. Facts now live on knowledge
  // pages, so the card only leads to Knowledge.
  if (output.proposal)
    return (
      <button className="ws-artifact-card" onClick={() => ws.navigate('knowledge')}>
        <WorkspaceIcon name="knowledge" size={21} />
        <span>
          <strong>{uiText('Company knowledge suggestion')}</strong>
          <small>
            {uiText('Company knowledge now lives on pages. Open Knowledge to see them.')}
          </small>
        </span>
        <WorkspaceIcon name="arrow" size={15} />
      </button>
    );
  if (output.skill)
    return (
      <button
        className="ws-artifact-card"
        onClick={() => ws.openCanvas({ kind: 'skill', id: output.skill!.id })}
      >
        <WorkspaceIcon name="skills" size={22} />
        <span>
          <strong>{output.skill.draft.name}</strong>
          <small>{formatCopy(skills.draftCard, { revision: output.skill.currentRevision })}</small>
        </span>
        <WorkspaceIcon name="arrow" size={15} />
      </button>
    );
  if (output.result)
    return (
      <>
        <button
          className="ws-artifact-card"
          onClick={() => ws.openCanvas({ kind: 'result', id: output.result!.id })}
        >
          <WorkspaceIcon
            name={
              output.result.kind === 'table'
                ? 'table'
                : output.result.kind === 'chart'
                  ? 'results'
                  : output.result.kind === 'deck'
                    ? 'card'
                    : 'file'
            }
            size={22}
          />
          <span>
            <strong>{output.result.title}</strong>
            <small>
              {uiRich('{value1} · Open beside this conversation', {
                value1: output.result.kind === 'deck' ? 'Presentation' : uiText('Saved result'),
              })}
            </small>
          </span>
          <WorkspaceIcon name="arrow" size={15} />
        </button>
        <ProjectLandedNote
          id={output.result.id}
          madeIn={output.result.threadId}
          visibility={output.result.visibility}
        />
      </>
    );
  return null;
}

/** Under work made in a chat that lives in a project: it landed there, and who can open it now.
 * Said to the chat's owner, whose work it is. Work made in another chat (a new version of it,
 * saved here) stays where it was made, so it says nothing. */
export function ProjectLandedNote({
  id,
  madeIn,
  visibility,
}: {
  id: string;
  madeIn: string | null | undefined;
  visibility?: CompanyResult['visibility'];
}) {
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const owner = (ws?.threadMeta?.access ?? 'owner') === 'owner';
  const place = ws && owner && ws.threadSaved && madeIn === ws.threadId ? chatProject(ws) : null;
  if (!ws || !place) return null;
  const now = ws.data.results.find((result) => result.id === id)?.visibility ?? visibility;
  return (
    <p className="ws-project-landed">
      <WorkspaceIcon name="project" size={13} />
      {formatCopy(copy.landedIn, {
        name: place.name,
        access:
          !now || now === 'private'
            ? copy.landedOnlyYou
            : now === 'company'
              ? formatCopy(copy.landedCompanyCanOpen, { company: ws.companyName })
              : copy.landedMembersCanOpen,
      })}
    </p>
  );
}

/** Where a person can finish a failed step by hand, by the tool's fixed id (DES1 HC-05). A tool
 *  with no honest next step has none; the vague "review and try again" suffix is gone. Every
 *  target is a view each member can open (personal settings, Agents, Connections). */
const ERROR_NEXT: Partial<Record<string, { label: ChatCopyKey; view: WorkspaceView }>> = {
  [SETTINGS_TOOL]: { label: 'toolErrorOpenSettings', view: 'preferences' },
  create_agent: { label: 'toolErrorOpenAgents', view: 'agents' },
  offer_connections: { label: 'toolErrorOpenConnections', view: 'connections' },
};

const keepsBrowserBehaviour = (event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}) => event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

function ToolErrorCard({ toolName }: { toolName: string }) {
  const ws = useOptionalWorkspace();
  const copy = useChatCopy();
  const next = ERROR_NEXT[toolName];
  return (
    <div className="ws-error" role="alert">
      {errorCopy(toolName, copy)}
      {next && ws && (
        <>
          {' '}
          <a
            className="ws-error-link"
            href={routeFor(next.view)}
            onClick={(event) => {
              // Ctrl/Cmd/Shift/Alt or a non-primary click keeps the browser's own behaviour
              // (a new tab or window), as the Connected agents link does.
              if (keepsBrowserBehaviour(event)) return;
              event.preventDefault();
              ws.navigate(next.view);
            }}
          >
            {copy[next.label]}
            <WorkspaceIcon name="arrow" size={13} />
          </a>
        </>
      )}
    </div>
  );
}

function ConnectedAgentsLoadError() {
  const copy = useNamespaceCopy(connectionsCopy);
  return (
    <div className="ws-error" role="alert">
      {copy.connectedAgentsLoadFailed}{' '}
      <a className="ws-error-link" href={SETUP_LINK} onClick={openSetupLink}>
        {copy.connectedAgentsOpen}
        {'\u00A0'}
        <WorkspaceIcon name="arrow" size={13} />
      </a>
    </div>
  );
}

// The one progress line that comes from the namespace, so it follows the person's language.
function ConnectedAgentsProgress() {
  return <>{useNamespaceCopy(connectionsCopy).checkingConnectedAgents}</>;
}
